import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

interface UseCopyReturnValue<Reference = any> {
  childRef: RefObject<Reference | null>;
  copied: boolean;
  handleClick: (
    event: React.MouseEvent<Reference, MouseEvent>,
    textToCopy: any,
  ) => Promise<void>;
}

const useCopy = <Reference = any>(): UseCopyReturnValue<Reference> => {
  const mounted = useRef(false);
  const childRef = useRef<Reference>(null);
  const [copied, setCopied] = useState(false);

  const handleClick = useCallback(
    async (_: React.MouseEvent<Reference, MouseEvent>, textToCopy: string) => {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(String(textToCopy));
      } else {
        const textarea = document.createElement('textarea');

        textarea.value = String(textToCopy);
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';

        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setCopied(true);
      mounted.current = true;

      if (mounted.current) {
        setCopied(false);
      }
    },
    [mounted],
  );

  useEffect(() => {
    return () => {
      mounted.current = false;
    };
  }, []);

  return { copied, handleClick, childRef };
};

export default useCopy;
